import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useSystem } from '@/contexts/SystemContext'
import { SystemSettings } from '@/types'
import { settingsService } from '@/services/settingsService'
import { Settings, AlertCircle, CheckCircle2, Webhook, Globe, TestTube, Play, Pause, Edit, Plus } from 'lucide-react'

const SystemSettingsTab: React.FC = () => {
  const { user } = useAuth()
  const { refreshSystemSettings } = useSystem()
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null)
  const [formData, setFormData] = useState({
    softwareName: ''
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [successMessage, setSuccessMessage] = useState('')

  // Webhook 相關狀態
  const [webhookLoading, setWebhookLoading] = useState(false)
  const [webhookUrl, setWebhookUrl] = useState('')
  const [webhookEvents, setWebhookEvents] = useState<Record<string, boolean>>({
    TIMER_STARTED: false,
    TIMER_STOPPED: false,
    TIMER_EDITED: false,
    TIMER_MANUAL_ADDED: false
  })
  const [testingWebhook, setTestingWebhook] = useState('')

  // 載入系統設定
  useEffect(() => {
    loadSystemSettings()
    loadWebhookSettings()
  }, [])

  const loadSystemSettings = async () => {
    if (user?.role !== 'ADMIN') return

    try {
      setIsInitialLoading(true)
      const settings = await settingsService.getSystemSettings()
      setSystemSettings(settings)
      setFormData({
        softwareName: settings.softwareName
      })
    } catch (error) {
      console.error('載入系統設定失敗:', error)
      setErrors({ general: '載入系統設定失敗，請重新整理頁面' })
    } finally {
      setIsInitialLoading(false)
    }
  }

  // 載入 Webhook 設定
  const loadWebhookSettings = async () => {
    if (user?.role !== 'ADMIN') return

    try {
      const settings = await settingsService.getWebhookSettings()
      setWebhookUrl(settings.defaultUrl || '')
      setWebhookEvents(settings.events || {
        TIMER_STARTED: false,
        TIMER_STOPPED: false,
        TIMER_EDITED: false,
        TIMER_MANUAL_ADDED: false
      })
    } catch (error) {
      console.error('載入 Webhook 設定失敗:', error)
    }
  }

  // 更新 Webhook 事件開關
  const handleWebhookEventToggle = async (eventType: string, enabled: boolean) => {
    try {
      setWebhookLoading(true)
      const newEvents = { ...webhookEvents, [eventType]: enabled }
      await settingsService.updateWebhookEvents(newEvents)
      setWebhookEvents(newEvents)
      setSuccessMessage(`${getEventDisplayName(eventType)} Webhook 已${enabled ? '啟用' : '停用'}`)
    } catch (error) {
      console.error('更新 Webhook 事件失敗:', error)
      setErrors({ webhook: '更新 Webhook 設定失敗，請稍後再試' })
    } finally {
      setWebhookLoading(false)
    }
  }

  // 更新 Webhook URL
  const handleWebhookUrlUpdate = async () => {
    if (!webhookUrl.trim()) {
      setErrors({ webhookUrl: 'Webhook URL 不能為空' })
      return
    }

    try {
      setWebhookLoading(true)
      setErrors({ webhookUrl: '' })
      await settingsService.updateDefaultWebhookUrl(webhookUrl.trim())
      setSuccessMessage('Webhook URL 已成功更新')
    } catch (error) {
      console.error('更新 Webhook URL 失敗:', error)
      setErrors({ webhookUrl: '更新 Webhook URL 失敗，請確認網址格式正確' })
    } finally {
      setWebhookLoading(false)
    }
  }

  // 測試 Webhook
  const handleTestWebhook = async (eventType?: string) => {
    try {
      setTestingWebhook(eventType || 'all')
      await settingsService.testWebhook(eventType)
      setSuccessMessage(`測試 Webhook ${eventType ? getEventDisplayName(eventType) : '所有事件'} 已發送`)
    } catch (error) {
      console.error('測試 Webhook 失敗:', error)
      setErrors({ webhook: '測試 Webhook 失敗，請檢查設定' })
    } finally {
      setTestingWebhook('')
    }
  }

  // 獲取事件顯示名稱
  const getEventDisplayName = (eventType: string) => {
    const eventNames: Record<string, string> = {
      TIMER_STARTED: '開始計時',
      TIMER_STOPPED: '暫停計時',
      TIMER_EDITED: '編輯計時',
      TIMER_MANUAL_ADDED: '手動新增計時'
    }
    return eventNames[eventType] || eventType
  }

  // 獲取事件圖示
  const getEventIcon = (eventType: string) => {
    const eventIcons: Record<string, React.ReactNode> = {
      TIMER_STARTED: <Play className="w-4 h-4 text-green-600" />,
      TIMER_STOPPED: <Pause className="w-4 h-4 text-red-600" />,
      TIMER_EDITED: <Edit className="w-4 h-4 text-blue-600" />,
      TIMER_MANUAL_ADDED: <Plus className="w-4 h-4 text-purple-600" />
    }
    return eventIcons[eventType] || <Webhook className="w-4 h-4" />
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.softwareName.trim()) {
      newErrors.softwareName = '軟體名稱不能為空'
    } else if (formData.softwareName.length > 50) {
      newErrors.softwareName = '軟體名稱長度不能超過 50 字元'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    setIsLoading(true)
    setSuccessMessage('')
    setErrors({})

    try {
      await settingsService.updateSoftwareName(formData.softwareName.trim())
      
      // 更新本地狀態
      setSystemSettings(prev => prev ? {
        ...prev,
        softwareName: formData.softwareName.trim()
      } : null)
      
      // 刷新系統上下文以更新全局狀態
      await refreshSystemSettings()
      
      setSuccessMessage('軟體名稱已成功更新！所有頁面的標題已同步更新。')
      
    } catch (error) {
      console.error('更新軟體名稱失敗:', error)
      setErrors({ 
        general: error instanceof Error ? error.message : '更新失敗，請稍後再試' 
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (systemSettings) {
      setFormData({
        softwareName: systemSettings.softwareName
      })
      setErrors({})
      setSuccessMessage('')
    }
  }

  // 權限檢查
  if (user?.role !== 'ADMIN') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-secondary-800 mb-2">權限不足</h3>
          <p className="text-secondary-600">只有系統管理員可以修改系統設定</p>
        </div>
      </div>
    )
  }

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-secondary-600 dark:text-secondary-400">載入系統設定中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* 成功訊息 */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle2 className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {/* 一般錯誤訊息 */}
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{errors.general}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* 軟體品牌設定區塊 */}
        <div className="card p-6">
          <div className="flex items-center mb-6">
            <Settings className="w-6 h-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-primary-800">
              軟體品牌設定
            </h2>
          </div>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                軟體名稱 *
              </label>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-secondary-600 mb-2">
                  <span>目前名稱：</span>
                  <span className="font-medium text-primary-700 ml-1">
                    {systemSettings?.softwareName || '載入中...'}
                  </span>
                </div>
                <input
                  type="text"
                  value={formData.softwareName}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, softwareName: e.target.value }))
                    // 清除錯誤訊息
                    if (errors.softwareName) {
                      setErrors(prev => ({ ...prev, softwareName: '' }))
                    }
                  }}
                  className={`input-primary ${errors.softwareName ? 'border-red-500 focus:border-red-500' : ''}`}
                  placeholder="輸入新的軟體名稱，例如：恆遠數位行銷"
                  maxLength={50}
                  required
                />
                {errors.softwareName && (
                  <p className="text-sm text-red-600">{errors.softwareName}</p>
                )}
                <div className="flex justify-between text-sm text-secondary-500">
                  <span>字元數量：{formData.softwareName.length}/50</span>
                </div>
              </div>
            </div>

            {/* 說明文字 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-blue-800 text-sm">
                  <p className="font-medium mb-1">修改說明：</p>
                  <ul className="space-y-1">
                    <li>• 修改後，所有頁面的標題和品牌名稱都會更新</li>
                    <li>• 包含側邊欄、頁面標題、登入頁面等位置</li>
                    <li>• 變更會立即生效，無需重新載入頁面</li>
                    <li>• 建議使用簡短且具有代表性的名稱</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Webhook 設定區塊 */}
        <div className="card p-6">
          <div className="flex items-center mb-6">
            <Webhook className="w-6 h-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-primary-800">
              Webhook 設定
            </h2>
          </div>

          {/* Webhook 錯誤訊息 */}
          {errors.webhook && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-red-800">{errors.webhook}</p>
              </div>
            </div>
          )}

          <div className="space-y-8">
            {/* Webhook URL 設定 */}
            <div>
              <div className="flex items-center mb-4">
                <Globe className="w-5 h-5 text-secondary-600 mr-2" />
                <h3 className="text-lg font-medium text-secondary-800">預設 Webhook URL</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Webhook 接收網址
                  </label>
                  <div className="flex space-x-3">
                    <input
                      type="url"
                      value={webhookUrl}
                      onChange={(e) => {
                        setWebhookUrl(e.target.value)
                        if (errors.webhookUrl) {
                          setErrors(prev => ({ ...prev, webhookUrl: '' }))
                        }
                      }}
                      className={`flex-1 input-primary ${errors.webhookUrl ? 'border-red-500 focus:border-red-500' : ''}`}
                      placeholder="https://your-domain.com/webhook"
                    />
                    <button
                      type="button"
                      onClick={handleWebhookUrlUpdate}
                      disabled={webhookLoading || !webhookUrl.trim()}
                      className="btn-primary px-4"
                    >
                      {webhookLoading ? (
                        <div className="loading-spinner"></div>
                      ) : (
                        '更新'
                      )}
                    </button>
                  </div>
                  {errors.webhookUrl && (
                    <p className="text-sm text-red-600 mt-1">{errors.webhookUrl}</p>
                  )}
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="text-blue-800 text-sm">
                      <p className="font-medium mb-1">Webhook 說明：</p>
                      <ul className="space-y-1">
                        <li>• 請提供可接收 POST 請求的有效網址</li>
                        <li>• Webhook 將包含 HMAC-SHA256 簽章以確保安全性</li>
                        <li>• 系統會在觸發事件時即時發送通知</li>
                        <li>• 建議使用 HTTPS 協議以確保資料安全</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Webhook 事件設定 */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Settings className="w-5 h-5 text-secondary-600 mr-2" />
                  <h3 className="text-lg font-medium text-secondary-800">事件設定</h3>
                </div>
                <button
                  type="button"
                  onClick={() => handleTestWebhook()}
                  disabled={testingWebhook === 'all' || !webhookUrl.trim()}
                  className="btn-secondary text-sm"
                >
                  {testingWebhook === 'all' ? (
                    <>
                      <div className="loading-spinner mr-2"></div>
                      測試中...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      測試所有事件
                    </>
                  )}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(webhookEvents).map(([eventType, isEnabled]) => (
                  <div key={eventType} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        {getEventIcon(eventType)}
                        <span className="ml-2 font-medium text-secondary-800">
                          {getEventDisplayName(eventType)}
                        </span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={(e) => handleWebhookEventToggle(eventType, e.target.checked)}
                          disabled={webhookLoading}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        isEnabled 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {isEnabled ? '已啟用' : '已停用'}
                      </span>
                      
                      <button
                        type="button"
                        onClick={() => handleTestWebhook(eventType)}
                        disabled={testingWebhook === eventType || !isEnabled || !webhookUrl.trim()}
                        className="text-primary-600 hover:text-primary-800 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center"
                      >
                        {testingWebhook === eventType ? (
                          <>
                            <div className="loading-spinner mr-1"></div>
                            測試中
                          </>
                        ) : (
                          <>
                            <TestTube className="w-4 h-4 mr-1" />
                            測試
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Webhook 資料格式說明 */}
            <div>
              <h3 className="text-lg font-medium text-secondary-800 mb-4">資料格式說明</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-secondary-700 mb-2">請求標頭</h4>
                    <div className="bg-white border rounded p-3 text-sm font-mono">
                      <div>Content-Type: application/json</div>
                      <div>X-Webhook-Signature: sha256=...</div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-secondary-700 mb-2">範例資料 (開始計時)</h4>
                    <div className="bg-white border rounded p-3 text-sm font-mono overflow-x-auto">
                      <pre>{`{
  "eventType": "TIMER_STARTED",
  "timestamp": "2024-01-01T12:00:00Z",
  "data": {
    "user": {
      "id": "1",
      "name": "使用者姓名",
      "email": "user@example.com"
    },
    "timeEntry": {
      "id": "123",
      "description": "任務名稱",
      "startTime": "2024-01-01T12:00:00Z",
      "projectId": "456"
    }
  }
}`}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 操作按鈕 */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleReset}
            disabled={isLoading}
            className="btn-secondary"
          >
            重置
          </button>
          <button
            type="submit"
            disabled={isLoading || !formData.softwareName.trim()}
            className="btn-primary"
          >
            {isLoading ? (
              <>
                <div className="loading-spinner mr-2"></div>
                更新中...
              </>
            ) : (
              '儲存變更'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default SystemSettingsTab
